import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchWithdrawals, fetchWalletUser } from "../modules/wallet/services/walletService";
import WithdrawalModal from "../modules/wallet/components/WithdrawalModal";
import "../css/WalletWithdrawal.css";
import "../css/AdManagement.css";
import "../css/WalletDashboard.css";
import Layout from "./Layout";

const LIMIT = 30;

const STATUS_LABEL = {
  REQUESTED: "요청접수",
  IN_REVIEW: "검토중",
  APPROVED: "승인",
  REJECTED: "거절",
  COMPLETED: "완료",
  CANCELED: "취소",
};

const STATUS_CLASS = {
  REQUESTED: "ww-status-requested",
  IN_REVIEW: "ww-status-review",
  APPROVED: "ww-status-approved",
  REJECTED: "ww-status-rejected",
  COMPLETED: "ww-status-completed",
  CANCELED: "ww-status-canceled",
};

const EMPTY_FILTER = { status: "", deadlineFilter: "ALL", userId: "", from: "", to: "" };

function formatDateTime(str) {
  if (!str) return "-";
  const d = new Date(str);
  if (isNaN(d)) return str;
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatAmount(amount) {
  if (amount == null) return "-";
  return amount.toLocaleString() + " 원";
}

function formatDeadline(businessDaysLeft, status) {
  if (["REJECTED", "COMPLETED", "CANCELED"].includes(status)) return { text: "처리완료", cls: "" };
  if (businessDaysLeft > 1) return { text: `D-${businessDaysLeft}`, cls: "" };
  if (businessDaysLeft === 1) return { text: "D-1", cls: "ww-deadline-imminent" };
  if (businessDaysLeft === 0) return { text: "D-DAY", cls: "ww-deadline-imminent" };
  return { text: `기한초과 +${Math.abs(businessDaysLeft)}일`, cls: "ww-deadline-overdue" };
}

function WalletWithdrawalManagement() {
  const navigate = useNavigate();
  const location = useLocation();
  const highlightId = location.state?.highlightId ?? null;
  const highlightUserId = location.state?.highlightUserId ?? null;
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [filter, setFilter] = useState({ ...EMPTY_FILTER });
  const [tempFilter, setTempFilter] = useState({ ...EMPTY_FILTER });
  const [modalWithdrawalId, setModalWithdrawalId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const result = await fetchWithdrawals({ ...filter, limit: LIMIT, offset });
        if (!cancelled) {
          setItems(result.items || []);
          setTotal(result.total || 0);
        }
      } catch {
        if (!cancelled) {
          setItems([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [filter, offset, refreshKey]);

  async function handleUserClick(userId) {
    try {
      const result = await fetchWalletUser(userId);
      navigate(`/wallet/user/${result.memberNo}`, { state: { prefetched: result } });
    } catch {
      alert('사용자 정보를 찾을 수 없습니다.');
    }
  }

  function handleSearch() {
    setFilter({ ...tempFilter });
    setOffset(0);
  }

  function handleReset() {
    setTempFilter({ ...EMPTY_FILTER });
    setFilter({ ...EMPTY_FILTER });
    setOffset(0);
  }

  function handleTempChange(e) {
    const { name, value } = e.target;
    setTempFilter((prev) => ({ ...prev, [name]: value }));
  }

  const totalPages = Math.ceil(total / LIMIT);
  const currentPage = Math.floor(offset / LIMIT) + 1;
  const startIndex = total === 0 ? 0 : offset + 1;
  const endIndex = Math.min(offset + LIMIT, total);

  return (
    <Layout>
      <div className="wallet-withdrawal">
        <h4 style={{ fontWeight: 700, color: "#1f2937", margin: 0 }}>출금 요청 관리</h4>

        <div className="wallet-withdrawal-filter">
          <select
            name="status"
            value={tempFilter.status}
            onChange={handleTempChange}
            style={{ width: 130 }}
          >
            <option value="">전체</option>
            <option value="REQUESTED">요청접수</option>
            {/* <option value="IN_REVIEW">검토중</option> */}
            <option value="APPROVED">승인</option>
            <option value="REJECTED">거절</option>
            <option value="COMPLETED">완료</option>
            {/* <option value="CANCELED">취소</option> */}
          </select>
          <select
            name="deadlineFilter"
            value={tempFilter.deadlineFilter}
            onChange={handleTempChange}
            style={{ width: 120 }}
          >
            <option value="ALL">전체</option>
            <option value="IMMINENT">기한임박</option>
            <option value="OVERDUE">기한초과</option>
          </select>
          <input
            type="text"
            name="userId"
            placeholder="사용자 ID"
            value={tempFilter.userId}
            onChange={handleTempChange}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            style={{ width: 140 }}
          />
          <input
            type="date"
            name="from"
            className="wallet-date-input"
            value={tempFilter.from}
            onChange={handleTempChange}
          />
          <span style={{ fontSize: "0.85rem", color: "#6b7280" }}>~</span>
          <input
            type="date"
            name="to"
            className="wallet-date-input"
            value={tempFilter.to}
            onChange={handleTempChange}
          />
          <button className="wallet-filter-action-btn wallet-filter-search" onClick={handleSearch}>
            검색
          </button>
          <button className="wallet-filter-action-btn wallet-filter-reset" onClick={handleReset}>
            초기화
          </button>
        </div>

        <div className="wallet-withdrawal-table-section">
          {loading ? (
            <div className="text-center text-muted py-4">불러오는 중...</div>
          ) : items.length === 0 ? (
            <div className="wallet-withdrawal-empty">조회된 데이터가 없습니다</div>
          ) : (
            <>
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>신청일</th>
                    <th>요청 ID</th>
                    <th>사용자</th>
                    <th>신청금액</th>
                    <th>수수료</th>
                    <th>실수령액</th>
                    <th>상태</th>
                    <th>처리기한</th>
                    <th>액션</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const deadline = formatDeadline(item.businessDaysLeft, item.status);
                    return (
                      <tr
                        key={item.pointWithdrawalRequestId}
                        className={(highlightId !== null && highlightId === item.pointWithdrawalRequestId) || (highlightUserId !== null && highlightUserId === item.userId) ? "wc-row-highlight" : ""}
                      >
                        <td>{formatDateTime(item.requestedAt)}</td>
                        <td>{item.pointWithdrawalRequestId}</td>
                        <td>
                          <button
                            onClick={() => handleUserClick(item.userId)}
                            style={{ background: 'none', border: 'none', padding: 0, color: '#6366f1', cursor: 'pointer', fontSize: 'inherit', fontWeight: 600, textDecoration: 'underline' }}
                          >
                            {item.userName || item.userId}
                          </button>
                        </td>
                        <td>{formatAmount(item.requestedAmount)}</td>
                        <td>{formatAmount(item.feeAmount)}</td>
                        <td>{formatAmount(item.payoutAmount)}</td>
                        <td>
                          <span className={`ww-status-badge ${STATUS_CLASS[item.status] || ""}`}>
                            {STATUS_LABEL[item.status] || item.status}
                          </span>
                        </td>
                        <td className={deadline.cls}>{deadline.text}</td>
                        <td>
                          <button
                            className="ww-action-btn"
                            onClick={() => setModalWithdrawalId(item.pointWithdrawalRequestId)}
                          >
                            상세
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div className="pagination-info">
                <div>
                  총 {total}개 항목 중 {startIndex} - {endIndex}
                </div>
                <div className="pagination">
                  <button
                    onClick={() => setOffset((prev) => Math.max(0, prev - LIMIT))}
                    disabled={currentPage === 1}
                  >
                    이전
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i + 1}
                      className={currentPage === i + 1 ? "active" : ""}
                      onClick={() => setOffset(i * LIMIT)}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => setOffset((prev) => prev + LIMIT)}
                    disabled={endIndex >= total}
                  >
                    다음
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <WithdrawalModal
          isOpen={modalWithdrawalId !== null}
          withdrawalId={modalWithdrawalId}
          onClose={() => setModalWithdrawalId(null)}
          onSuccess={() => {
            setModalWithdrawalId(null);
            setRefreshKey((k) => k + 1);
          }}
        />
      </div>
    </Layout>
  );
}

export default WalletWithdrawalManagement;
